import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import mermasService from '../services/mermasService';
import {
  CrearMermaDTO,
  CrearMermasMultiplesDTO,
  AprobarMermaDTO,
  RechazarMermaDTO,
  ActualizarMermaDTO,
  FiltrosMerma,
  FiltrosSugerencias,
  FiltrosEstadisticas,
} from '../types/mermas';
import { useToast } from './useToast';

// Hook para obtener mermas con paginación y filtros
export const useMermas = (filtros: FiltrosMerma = {}) => {
  return useQuery({
    queryKey: ['mermas', filtros],
    queryFn: async () => {
      const response = await mermasService.getAll(filtros);
      return response;
    },
    // Fix tanda3 H7 (2026-07-01): staleTime era 2 min → el contador
    // "Total: X mermas" (usa `mermasData.pagination.total`) no
    // reflejaba las mermas recién creadas. `invalidateQueries` marcaba
    // stale pero React Query servía cache. Bajado a 0 para que el
    // total se refresque tras cada create.
    staleTime: 0,
  });
};

// Hook para obtener una merma por ID
export const useMerma = (id: number) => {
  return useQuery({
    queryKey: ['mermas', id],
    queryFn: async () => {
      const response = await mermasService.getById(id);
      return response.data;
    },
    enabled: !!id,
  });
};

// Hook para obtener motivos de merma
export const useMotivos = () => {
  return useQuery({
    queryKey: ['mermas', 'motivos'],
    queryFn: async () => {
      const response = await mermasService.getMotivos();
      return response.data;
    },
    staleTime: 1000 * 60 * 30, // 30 minutos (los motivos cambian poco)
  });
};

// Hook para obtener sugerencias de merma
export const useSugerencias = (filtros: FiltrosSugerencias = {}) => {
  return useQuery({
    queryKey: ['mermas', 'sugerencias', filtros],
    queryFn: async () => {
      const response = await mermasService.getSugerencias(filtros);
      return response.data;
    },
    // Fix tanda3 H5 (2026-07-01): staleTime era 5 min → tras crear una
    // merma, el `invalidateQueries({queryKey:['mermas']})` marcaba la
    // query stale pero React Query servía cache si el usuario cerraba
    // y reabría el panel dentro de los 5 min. Bajado a 0 para que
    // cualquier invalidación dispare refetch inmediato de sugerencias.
    staleTime: 0,
  });
};

// Hook para obtener estadísticas de mermas
export const useEstadisticas = (filtros: FiltrosEstadisticas = {}) => {
  return useQuery({
    queryKey: ['mermas', 'estadisticas', filtros],
    queryFn: async () => {
      const response = await mermasService.getEstadisticas(filtros);
      return response.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutos
  });
};

// Hook para crear merma
export const useCreateMerma = () => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation({
    mutationFn: (payload: CrearMermaDTO) => mermasService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mermas'] });
      queryClient.invalidateQueries({ queryKey: ['lotes'] });
      success('Merma creada', 'La merma se ha registrado correctamente');
    },
    onError: (err: unknown) => {
      // Fix tanda3 H6 (2026-07-01): el backend responde con `message`
      // (ej. "Cantidad insuficiente en lote") pero acá se leía `error`
      // → toast decía "Ha ocurrido un error inesperado" y el usuario
      // no sabía qué había fallado. Ahora respetamos ambas keys.
      console.error('Error al crear merma:', err);
      const anyErr = err as { response?: { data?: { message?: string; error?: string } } };
      error(
        'No se pudo crear la merma',
        anyErr.response?.data?.message ||
          anyErr.response?.data?.error ||
          'Ha ocurrido un error inesperado'
      );
    },
  });
};

// Hook para crear múltiples mermas
export const useCreateMermasMultiples = () => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation({
    mutationFn: (payload: CrearMermasMultiplesDTO) => mermasService.createMultiples(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mermas'] });
      queryClient.invalidateQueries({ queryKey: ['lotes'] });
      success('Mermas creadas', 'Las mermas se han registrado correctamente');
    },
    onError: (err: unknown) => {
      // Fix tanda3 H6 (2026-07-01): mismo issue que useCreateMerma
      console.error('Error al crear mermas:', err);
      const anyErr = err as { response?: { data?: { message?: string; error?: string } } };
      error(
        'No se pudieron crear las mermas',
        anyErr.response?.data?.message ||
          anyErr.response?.data?.error ||
          'Ha ocurrido un error inesperado'
      );
    },
  });
};

// Hook para aprobar merma
export const useAprobarMerma = () => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: AprobarMermaDTO }) =>
      mermasService.aprobar(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mermas'] });
      queryClient.invalidateQueries({ queryKey: ['lotes'] });
      success('Merma aprobada', 'La merma se ha aprobado correctamente');
    },
    onError: (err: any) => {
      console.error('Error al aprobar merma:', err);
      error(
        'Error al aprobar merma',
        err.response?.data?.error || 'Ha ocurrido un error inesperado'
      );
    },
  });
};

// Hook para rechazar merma
export const useRechazarMerma = () => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: RechazarMermaDTO }) =>
      mermasService.rechazar(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mermas'] });
      success('Merma rechazada', 'La merma se ha rechazado correctamente');
    },
    onError: (err: any) => {
      console.error('Error al rechazar merma:', err);
      error(
        'Error al rechazar merma',
        err.response?.data?.error || 'Ha ocurrido un error inesperado'
      );
    },
  });
};

// Hook para actualizar merma
export const useUpdateMerma = () => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ActualizarMermaDTO }) =>
      mermasService.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mermas'] });
      success('Merma actualizada', 'La merma se ha actualizado correctamente');
    },
    onError: (err: any) => {
      console.error('Error al actualizar merma:', err);
      error(
        'Error al actualizar merma',
        err.response?.data?.error || 'Ha ocurrido un error inesperado'
      );
    },
  });
};

// Hook para eliminar merma
export const useDeleteMerma = () => {
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  return useMutation({
    mutationFn: (id: number) => mermasService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mermas'] });
      success('Merma eliminada', 'La merma se ha eliminado correctamente');
    },
    onError: (err: any) => {
      console.error('Error al eliminar merma:', err);
      error(
        'Error al eliminar merma',
        err.response?.data?.error || 'Ha ocurrido un error inesperado'
      );
    },
  });
};
